import * as AzureSpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import { createSpeechRecognitionPonyfillFromRecognizer } from 'web-speech-cognitive-services';

// Debug flag - set to true to enable console.log statements
const DEBUG_AZURE_SPEECH = false;

// Debug logging wrapper function
const debugLog = (..._args: any[]) => {
  if (DEBUG_AZURE_SPEECH) console.warn(..._args);
};

export interface AzureTokenResponse {
  accessToken: string;
  hostname?: string;
  region: string;
  endpointUrl?: string;
}

export function createAzureSpeechRecognitionPonyfill(tokenRetrievalFunction: () => Promise<AzureTokenResponse>): ReturnType<typeof createSpeechRecognitionPonyfillFromRecognizer> {
  debugLog('[Azure Speech] Creating Azure Speech Recognition Ponyfill');

  debugLog('[Azure Speech] Attempting to create audio config from default microphone');
  const audioConfig = AzureSpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
  debugLog('[Azure Speech] Audio config created:', !!audioConfig);

  if (!audioConfig && (!window.navigator.mediaDevices || !window.navigator.mediaDevices.getUserMedia)) {
    throw new Error(
      'web-speech-cognitive-services: This browser does not support Media Capture and Streams API and it will not work with Cognitive Services Speech Services.',
    );
  }

  async function createAzureSpeechRecognizer(lang: string) {
    debugLog('[Azure Speech] Creating recognizer for language:', lang);

    const res = await tokenRetrievalFunction();
    debugLog('[Azure Speech] Token retrieved, hostname:', res.hostname, 'region:', res.region);
    let speechConfig;

    if (res.hostname) {
      const url = new URL(`wss://${res.hostname}`);
      debugLog('Azure Speech Services URL:', url.href);
      speechConfig = AzureSpeechSDK.SpeechConfig.fromEndpoint(url);
      speechConfig.authorizationToken = res.accessToken;
    } else {
      debugLog('[Azure Speech] Using region-based config');
      speechConfig = AzureSpeechSDK.SpeechConfig.fromAuthorizationToken(res.accessToken, res.region);
    }

    speechConfig.outputFormat = AzureSpeechSDK.OutputFormat.Detailed;
    speechConfig.speechRecognitionLanguage = lang || 'en-US';

    speechConfig.setProperty(AzureSpeechSDK.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs, '3000');
    speechConfig.setProperty(AzureSpeechSDK.PropertyId.SpeechServiceConnection_EndSilenceTimeoutMs, '3000');
    speechConfig.setProperty(AzureSpeechSDK.PropertyId.Speech_SegmentationSilenceTimeoutMs, '3000');

    debugLog('[Azure Speech] Creating SpeechRecognizer instance');
    return new AzureSpeechSDK.SpeechRecognizer(speechConfig, audioConfig);
  }

  debugLog('[Azure Speech] Creating ponyfill from recognizer');
  return createSpeechRecognitionPonyfillFromRecognizer({
    createRecognizer: createAzureSpeechRecognizer,
    enableTelemetry: undefined,
    looseEvents: false,
    textNormalization: 'display',
  });
}
